import {
  InformationCircleIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import { Headset } from 'lucide-react';

import { useTranslation } from '@/app/providers';
import { SectionNotEnabledIllustration } from '@/shared/components/section-not-enabled/section-not-enabled-illustration';
import type { TranslationKey } from '@/shared/i18n';
import { cn } from '@/shared/lib/cn';

export type SectionNotEnabledTranslationPrefix =
  | 'whatsAppOfficial.disabled'
  | 'menu.disabled'
  | 'subscription.expired'
  | 'automation.disabled'
  | 'orders.disabled'
  | 'delivery.disabled';

interface SectionNotEnabledStateProps {
  translationPrefix: SectionNotEnabledTranslationPrefix;
  /** Stretch the card to fill available parent height (e.g. full content area). */
  fillHeight?: boolean;
}

function disabledKey(
  prefix: SectionNotEnabledTranslationPrefix,
  suffix:
    | 'badge'
    | 'title'
    | 'description'
    | 'infoTitle'
    | 'infoDescription'
    | 'footerHelp'
    | 'footerHelpDesc',
): TranslationKey {
  return `${prefix}.${suffix}` as TranslationKey;
}

export function SectionNotEnabledState({
  translationPrefix,
  fillHeight = false,
}: SectionNotEnabledStateProps) {
  const { t } = useTranslation();

  return (
    <div
      className={cn(
        'overflow-hidden rounded-xl border border-content-border bg-card',
        fillHeight && 'flex h-full min-h-0 flex-1 flex-col',
      )}
    >
      <div
        className={cn(
          'grid gap-8 p-6 sm:p-8 lg:grid-cols-2 lg:items-center lg:gap-10 lg:p-10',
          fillHeight && 'flex-1',
        )}
      >
        <div className="flex flex-col gap-6">
          <div className="flex h-11 w-11 items-center justify-center rounded-sm border border-primary/15 bg-primary/10 text-primary">
            <LockClosedIcon className="h-5 w-5" aria-hidden />
          </div>

          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-primary">
              {t(disabledKey(translationPrefix, 'badge'))}
            </p>
            <h2 className="text-2xl font-bold tracking-tight text-body sm:text-3xl">
              {t(disabledKey(translationPrefix, 'title'))}
            </h2>
            <p className="max-w-md text-base text-secondary">
              {t(disabledKey(translationPrefix, 'description'))}
            </p>
          </div>

          <div className="flex gap-3 rounded-lg border border-primary/15 bg-primary/5 p-4">
            <InformationCircleIcon
              className="mt-0.5 h-5 w-5 shrink-0 text-primary"
              aria-hidden
            />
            <div className="space-y-1">
              <p className="text-sm font-semibold text-body">
                {t(disabledKey(translationPrefix, 'infoTitle'))}
              </p>
              <p className="text-sm text-secondary">
                {t(disabledKey(translationPrefix, 'infoDescription'))}
              </p>
            </div>
          </div>
        </div>

        <div className="mx-auto w-full max-w-lg lg:max-w-none">
          <SectionNotEnabledIllustration className="h-auto w-full" />
        </div>
      </div>

      <div className="flex items-start gap-2.5 border-t border-content-border bg-muted/50 px-6 py-4 text-sm text-secondary sm:items-center sm:px-8 lg:px-10">
        <Headset
          className="mt-0.5 h-4 w-4 shrink-0 text-secondary sm:mt-0"
          aria-hidden
        />
        <p>
          <span className="font-semibold text-body">
            {t(disabledKey(translationPrefix, 'footerHelp'))}
          </span>{' '}
          {t(disabledKey(translationPrefix, 'footerHelpDesc'))}
        </p>
      </div>
    </div>
  );
}
